import { KeyboardEvent } from 'react';

/** How Enter should submit the form. `auto` picks based on whether the form has a textarea. */
export type EnterToSubmitMode = false | 'simple' | 'combined' | 'auto';

/** Resolved enter-to-submit mode after expanding `auto`. */
export type ResolvedEnterToSubmitMode = 'simple' | 'combined';

type Options = {
  /** Submit mode. Defaults to `auto`. */
  mode?: EnterToSubmitMode;
  /** When false, Enter shortcuts do nothing. Defaults to true. */
  enabled?: boolean;
};

/**
 * Finds the form associated with a keydown scope element.
 * Supports the form itself, a descendant focus container, or an ancestor wrapper.
 */
export function getEnterToSubmitForm(currentTarget: EventTarget | null): HTMLFormElement | null {
  if (!(currentTarget instanceof HTMLElement)) {
    return null;
  }

  if (currentTarget instanceof HTMLFormElement) {
    return currentTarget;
  }

  return currentTarget.closest('form') ?? currentTarget.querySelector('form');
}

/**
 * Resolves `auto` against the form DOM: `combined` when a textarea exists, otherwise `simple`.
 * Returns `false` when enter-to-submit is disabled.
 */
export function resolveEnterToSubmitMode(
  mode: EnterToSubmitMode,
  form: HTMLFormElement | null,
): ResolvedEnterToSubmitMode | false {
  if (!mode) {
    return false;
  }

  if (mode === 'auto') {
    return form?.querySelector('textarea') ? 'combined' : 'simple';
  }

  return mode;
}

/**
 * Returns the HotkeyDisplay key string for a resolved enter-to-submit mode.
 * Combined uses platform variants so HotkeyDisplay can pick Ctrl vs Command.
 */
export function getEnterToSubmitHotkey(mode: ResolvedEnterToSubmitMode): string {
  return mode === 'combined' ? 'ctrl+enter,command+enter' : 'enter';
}

/**
 * Returns a keydown handler that submits on Enter.
 * Can be attached to the `<form>` or to a focusable scope container around/inside it.
 * - `simple`: Enter submits (except inside textareas)
 * - `combined`: Ctrl/Cmd+Enter submits; plain Enter is blocked on non-textarea fields
 * - `auto`: `combined` if the form contains a textarea, otherwise `simple`
 * - `false`: disabled
 */
export function useEnterToSubmit({ mode = 'auto', enabled = true }: Options = {}) {
  return (event: KeyboardEvent<HTMLElement>) => {
    if (!enabled || event.key !== 'Enter' || event.nativeEvent.isComposing) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target.closest('button, [role="button"]')) {
      return;
    }

    const form = getEnterToSubmitForm(event.currentTarget);
    if (!form) {
      return;
    }

    const resolvedMode = resolveEnterToSubmitMode(mode, form);

    if (!resolvedMode) {
      return;
    }

    const isTextarea = target.tagName === 'TEXTAREA';

    if (resolvedMode === 'combined') {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        event.stopPropagation();
        form.requestSubmit();
      } else if (!isTextarea) {
        // Avoid native Enter submit on inputs when the form also has textareas
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    // simple: Enter submits from inputs; leave textarea newline behavior alone
    if (!isTextarea && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      form.requestSubmit();
    }
  };
}
